// TASK-48 (S2, hoist from apps/api/src/routes/v1/visit-report-schema.ts).
// This file owns:
//   1. The persisted DTO shapes (VisitReport / MedicationAdherenceEntry /
//      VitalReading / VisitReportConflict / ValidationFlag) — pre-existing.
//   2. The wire validation Zod schemas + per-section pick schemas +
//      cross-field validators — hoisted at TASK-48 S2 to make
//      packages/core the single source of truth for the visit-report
//      validation surface. Both apps/api (re-export shim) and apps/web
//      (5 mirror sites) consume these schemas.
//
// References:
//   docs/specs/task-48-schema-architecture.md (binding contract)
//   docs/projects/task-48-zod-hoist.md (project brief)
//   packages/core/src/types/notification.ts (Q12 D1b co-location precedent)
import { z } from "zod";
// ── Wire validation schemas (TASK-48 S2 hoist) ─────────────────────────────
//
// Aligned with DRS B1-B7 and all P0-P3 gap-fill fields. Single source of
// truth for inbound validation; consumed by apps/api (handlers + per-section
// PATCH routes) and apps/web (FIELDS map, FIELD_TO_SECTION map, payload
// satisfies-typing, section-component apiFieldName props).
export const VITALS_FIELDS = ["bp_systolic", "bp_diastolic", "heart_rate", "spo2_percent",
    "temperature_celsius", "blood_glucose_mmol", "weight_kg", "respiratory_rate"];
const notTaken = z.enum(["no_equipment", "patient_refused", "patient_asleep",
    "clinical_contraindication", "not_applicable", "poor_reading", "no_strips",
    "unable_to_stand", "unable_to_assess"]).nullable().optional();
export const submitReportSchema = z.object({
    sr_id: z.string().uuid(),
    completed_at: z.string().datetime(),
    submission_channel: z.enum(["crm", "mobile", "whatsapp", "api"]),
    visit_type: z.enum(["standard_wellness", "extended_care", "companion", "post_discharge", "emergency_followup"]).nullable().optional(),
    visit_location: z.enum(["patient_home", "clinic", "hospital", "telehealth"]).nullable().optional(),
    assigned_carer_id_at_submission: z.string().uuid().nullable().optional(),
    form_open_at: z.string().datetime().nullable().optional(),
    // Vitals (DRS B2) + not-taken reason per vital
    bp_systolic: z.number().nullable().optional(),
    bp_diastolic: z.number().nullable().optional(),
    bp_position: z.enum(["sitting", "standing", "lying"]).nullable().optional(),
    bp_not_taken_reason: notTaken,
    heart_rate: z.number().nullable().optional(),
    heart_rate_rhythm: z.enum(["regular", "irregular", "unable_to_assess"]).nullable().optional(),
    hr_not_taken_reason: notTaken,
    spo2_percent: z.number().nullable().optional(),
    spo2_not_taken_reason: notTaken,
    temperature_celsius: z.number().nullable().optional(),
    temp_not_taken_reason: notTaken,
    blood_glucose_mmol: z.number().nullable().optional(),
    blood_glucose_timing: z.enum(["fasting", "post_meal_1hr", "post_meal_2hr", "random"]).nullable().optional(),
    glucose_not_taken_reason: notTaken,
    weight_kg: z.number().nullable().optional(),
    weight_not_taken_reason: notTaken,
    respiratory_rate: z.number().nullable().optional(),
    resp_not_taken_reason: notTaken,
    bilateral_ankle_oedema: z.enum(["none", "mild", "moderate", "severe"]).nullable().optional(),
    // Functional & Cognitive (DRS B3)
    mobility_status: z.enum(["independent", "uses_stick", "uses_walker", "uses_wheelchair", "needs_physical_assist", "bedbound"]).nullable().optional(),
    mobility_vs_last_visit: z.enum(["improved", "same", "declined", "first_visit"]).nullable().optional(),
    ambulation_status: z.enum(["independent_walking", "walks_with_stick", "walks_with_walker", "wheelchair", "non_ambulatory"]).nullable().optional(),
    adl_score: z.number().int().min(0).max(6).nullable().optional(),
    cognitive_status: z.enum(["alert_oriented", "mildly_confused", "moderately_confused", "severely_confused", "unresponsive"]).nullable().optional(),
    cognitive_vs_last_visit: z.enum(["improved", "same", "declined", "first_visit"]).nullable().optional(),
    cognitive_assessment_interpretation: z.enum(["normal", "mild_decline", "moderate_decline", "severe_decline"]).nullable().optional(),
    self_care_ability: z.enum(["independent", "needs_prompting", "needs_assistance", "fully_dependent"]).nullable().optional(),
    communication_ability: z.enum(["normal", "reduced_verbal", "non_verbal", "unresponsive"]).nullable().optional(),
    continence_status_visit: z.enum(["continent", "occasional_leakage", "frequent_incontinence", "catheterised"]).nullable().optional(),
    grooming_status: z.enum(["self_grooming", "needs_help", "fully_dependent"]).nullable().optional(),
    medication_admin_capacity: z.enum(["self_admin", "needs_reminder", "needs_supervision", "fully_dependent"]).nullable().optional(),
    cognitive_test_used: z.enum(["not_done", "mmse", "moca", "mini_cog", "moca_short_form", "clock_drawing", "informal_assessment", "other", "unable_to_assess"]).nullable().optional(),
    cognitive_test_score: z.number().int().min(0).max(150).nullable().optional(),
    // Symptoms & Wellbeing (DRS B4)
    mood_affect: z.enum(["positive", "neutral", "mildly_withdrawn", "significantly_withdrawn", "agitated", "distressed"]).nullable().optional(),
    appetite_today: z.enum(["good", "fair", "poor_reduced_intake", "refused_food", "unable_to_assess"]).nullable().optional(),
    fluid_intake_adequate: z.enum(["adequate", "reduced", "very_poor", "refused", "unable_to_assess"]).nullable().optional(),
    pain_present: z.boolean().optional().default(false),
    pain_severity: z.number().min(1).max(10).nullable().optional(),
    pain_location: z.enum(["chest", "abdomen", "head", "back", "limbs", "joints", "generalised", "other"]).nullable().optional(),
    pain_vs_last_visit: z.enum(["new_onset", "worsened", "same", "improved", "resolved", "first_visit"]).nullable().optional(),
    breathlessness_present: z.boolean().optional().default(false),
    breathlessness_severity: z.enum(["on_exertion_only", "at_rest_mild", "at_rest_severe"]).nullable().optional(),
    breathlessness_timing: z.enum(["on_exertion_only", "at_rest_mild", "at_rest_severe", "constant"]).nullable().optional(),
    eating_status: z.enum(["eating_normally", "reduced_appetite", "refusing_food", "difficulty_swallowing", "tube_fed", "not_assessed"]).nullable().optional(),
    sleep_quality_reported: z.enum(["good", "fair", "poor", "not_asked"]).nullable().optional(),
    bowel_function: z.enum(["normal", "constipated", "diarrhoea", "not_assessed"]).nullable().optional(),
    urinary_function: z.enum(["normal", "frequency_urgency", "burning_pain", "reduced_output", "not_assessed"]).nullable().optional(),
    skin_condition: z.enum(["normal", "pressure_area_developing", "pressure_sore_present", "wound_present", "bruising", "not_assessed"]).nullable().optional(),
    unplanned_weight_loss_reported: z.boolean().optional().default(false),
    fever_or_night_sweats: z.boolean().optional().default(false),
    persistent_cough: z.boolean().optional().default(false),
    dizziness_reported: z.boolean().optional().default(false),
    nausea_or_vomiting: z.boolean().optional().default(false),
    unusual_fatigue_reported: z.boolean().optional().default(false),
    // Incidents & Safety (DRS B5)
    fall_during_visit: z.boolean().optional().default(false),
    fall_since_last_visit_reported: z.boolean().optional().default(false),
    fall_count_since_last_visit: z.number().int().min(0).max(10).nullable().optional(),
    fall_injury_sustained: z.boolean().nullable().optional(),
    fall_injury_description: z.enum(["no_injury", "bruising_only", "laceration", "suspected_fracture", "head_injury"]).nullable().optional(),
    near_miss_reported: z.boolean().optional().default(false),
    patient_expressed_safety_concern: z.boolean().optional().default(false),
    safeguarding_concern_observed: z.boolean().optional().default(false),
    medication_error_observed: z.boolean().optional().default(false),
    wandering_behaviour_reported: z.boolean().optional().default(false),
    escalation_severity: z.enum(["low", "medium", "high", "critical"]).nullable().optional(),
    home_condition_concern_type: z.enum(["loose_rugs", "poor_lighting", "clutter", "unsafe_stairs", "no_grab_rails", "structural_hazard", "other"]).nullable().optional(),
    medication_list_verified: z.boolean().nullable().optional(),
    // Overall Assessment (DRS B7)
    overall_patient_status: z.enum(["stable_as_expected", "minor_concerns_monitor", "noticeable_change_cs_review", "significant_concern_gp_recommended", "urgent_immediate_action"]),
    patient_engagement_level: z.enum(["fully_engaged", "cooperative_but_limited", "minimally_engaged", "refused_all_assessments"]).nullable().optional(),
    environment_safety_concern: z.boolean().optional().default(false),
    escalation_flag: z.boolean().optional().default(false),
    // Medication Adherence (DRS B6)
    symptom_detail: z.object({
        unplanned_weight_loss_reported: z.string().max(300).optional(),
        fever_or_night_sweats: z.string().max(300).optional(),
        persistent_cough: z.string().max(300).optional(),
        dizziness_reported: z.string().max(300).optional(),
        nausea_or_vomiting: z.string().max(300).optional(),
        unusual_fatigue_reported: z.string().max(300).optional(),
    }).nullable().optional(),
    medication_adherence_log: z.array(z.object({
        medication_id: z.string().optional(),
        drug_name: z.string(),
        scheduled: z.boolean().optional().default(true),
        adherence_status: z.enum(["compliant_all_doses", "missed_one_dose", "missed_multiple_doses", "patient_refused", "not_available_pharmacy", "unable_to_assess"]),
        doses_missed_count: z.number().nullable().optional().default(null),
        adherence_verified_method: z.enum(["pill_count", "patient_report", "blister_pack_check", "carer_observation"]).nullable().optional().default(null),
        refused_reason: z.string().nullable().optional().default(null),
        is_critical: z.boolean().nullable().optional().default(null),
    })).optional().default([]),
    adherence_category_visit_summary: z.enum(["excellent", "good", "fair", "poor", "unable_to_assess"]).nullable().optional(),
    carer_recommendation: z.array(z.string()).optional().default([]),
    city_aqi_raw: z.number().int().min(0).max(500).nullable().optional(),
    pollution_exposure_flag: z.boolean().nullable().optional(),
    heat_severity_category: z.enum(["none", "mild", "moderate", "severe"]).nullable().optional(),
    // Q9 S6 / TASK-46 — relaxed from 500 to 1000 chars to match the
    // NotesSection.tsx UI cap. The 500 limit was an accidental default
    // (no compliance rationale in git history); "supplementary_notes"
    // implies extended capacity. notesPatchSchema inherits via .pick().
    supplementary_notes: z.string().max(1000).nullable().optional(),
    gps_lat: z.number().min(-90).max(90).nullable().optional(),
    gps_lng: z.number().min(-180).max(180).nullable().optional(),
    gps_accuracy_m: z.number().positive().nullable().optional(),
});
/** Cross-field validation run after Zod parse — returns field errors, or [] if none. */
export function validateCrossFields(data) {
    const errors = [];
    if (data.bp_systolic != null && data.bp_position == null) {
        errors.push({ field: "bp_position", error: "bp_position is required when bp_systolic is provided" });
    }
    if (data.escalation_flag && data.escalation_severity == null) {
        errors.push({ field: "escalation_severity", error: "escalation_severity is required when escalation_flag is true" });
    }
    return errors;
}
/** Strip cognitive score/interpretation when no cognitive test was performed. */
export function sanitizeClinicalContext(data) {
    const noTest = !data.cognitive_test_used || data.cognitive_test_used === "not_done";
    if (!noTest)
        return data;
    return { ...data, cognitive_test_score: null, cognitive_assessment_interpretation: null };
}
/** Strip breathlessness_severity and breathlessness_timing when breathlessness_present is false. */
export function sanitizeWellbeingFields(data) {
    if (data.breathlessness_present)
        return data;
    return { ...data, breathlessness_severity: null, breathlessness_timing: null };
}
// ── Q9 per-section Zod schemas (D16 + D22 picks of submitReportSchema) ──
// Each section's PATCH body is a strict subset of the monolithic schema.
// `pick()` preserves field-level rules + enum validation; cross-section
// rules remain on `validateCrossFields()` (submit-only per Q9 D7).
export const vitalsPatchSchema = submitReportSchema.pick({
    bp_systolic: true, bp_diastolic: true, bp_position: true, bp_not_taken_reason: true,
    heart_rate: true, heart_rate_rhythm: true, hr_not_taken_reason: true,
    spo2_percent: true, spo2_not_taken_reason: true,
    temperature_celsius: true, temp_not_taken_reason: true,
    blood_glucose_mmol: true, blood_glucose_timing: true, glucose_not_taken_reason: true,
    weight_kg: true, weight_not_taken_reason: true,
    respiratory_rate: true, resp_not_taken_reason: true,
    bilateral_ankle_oedema: true,
});
export const mobilityPatchSchema = submitReportSchema.pick({
    mobility_status: true, mobility_vs_last_visit: true,
    fall_during_visit: true, fall_since_last_visit_reported: true,
    fall_count_since_last_visit: true, fall_injury_sustained: true,
    fall_injury_description: true, near_miss_reported: true,
    patient_expressed_safety_concern: true, safeguarding_concern_observed: true,
    medication_error_observed: true, wandering_behaviour_reported: true,
    escalation_severity: true,
    environment_safety_concern: true, home_condition_concern_type: true,
});
// Q9 D1 lock — Wellbeing is its OWN conditional section (not merged into
// Clinical). Clinical retains the clinical-assessment subset; Wellbeing
// owns DRS §B4 ADL signals + symptom flags.
export const clinicalPatchSchema = submitReportSchema.pick({
    pain_present: true, pain_severity: true, pain_location: true, pain_vs_last_visit: true,
    ambulation_status: true, adl_score: true,
    continence_status_visit: true, grooming_status: true, medication_admin_capacity: true,
    cognitive_status: true, cognitive_vs_last_visit: true,
    cognitive_test_used: true, cognitive_test_score: true,
    cognitive_assessment_interpretation: true,
    mood_affect: true, appetite_today: true,
    overall_patient_status: true, patient_engagement_level: true,
    escalation_flag: true,
}).partial();
// Q9 D1 lock — Wellbeing as its own conditional step. DRS §B4 ADL +
// symptom subset. Co-fires with Clinical when visit_type !== "standard_wellness".
export const wellbeingPatchSchema = submitReportSchema.pick({
    breathlessness_present: true, breathlessness_severity: true, breathlessness_timing: true,
    eating_status: true, fluid_intake_adequate: true,
    sleep_quality_reported: true, bowel_function: true, urinary_function: true,
    skin_condition: true, self_care_ability: true, communication_ability: true,
    unplanned_weight_loss_reported: true, fever_or_night_sweats: true,
    persistent_cough: true, dizziness_reported: true,
    nausea_or_vomiting: true, unusual_fatigue_reported: true,
    symptom_detail: true,
});
export const medicationPatchSchema = submitReportSchema.pick({
    medication_adherence_log: true, medication_list_verified: true,
    adherence_category_visit_summary: true,
});
export const notesPatchSchema = submitReportSchema.pick({
    supplementary_notes: true, carer_recommendation: true,
});
// Q9 D19 — submit body is a small terminal payload. Cross-section gates
// run against the persisted Draft, not against this body.
//
// Q9 S4 — submit MUST declare its visible step list. The backend uses it
// to scope structured_report_complete + section completeness checks to
// the sections the wizard actually surfaced for this carer (per the
// conditional reveal rules in apps/web/src/lib/visit-report-conditional-rules.ts).
// The frontend computes visibleSteps client-side (D5); the backend is a
// trust-boundary verifier here, not a re-evaluator (D5 stops the rules
// from being duplicated server-side). vitals + notes are always required
// regardless of what visible_steps says (defensive — brief mandates them).
const SECTION_NAMES_LIST = ["vitals", "mobility", "clinical", "wellbeing", "medication", "notes"];
export const submitPatchSchema = z.object({
    completed_at: z.string().datetime(),
    submission_channel: z.enum(["crm", "mobile", "whatsapp", "api"]).optional().default("crm"),
    visible_steps: z.array(z.enum(SECTION_NAMES_LIST)).min(1),
    visit_location: z.enum(["patient_home", "clinic", "hospital", "telehealth"]).nullable().optional(),
    gps_lat: z.number().min(-90).max(90).nullable().optional(),
    gps_lng: z.number().min(-180).max(180).nullable().optional(),
    gps_accuracy_m: z.number().positive().nullable().optional(),
});
//# sourceMappingURL=visit-report.js.map